<template>
  <div>
    <b-card no-body class="distribution-card">
      <div class="card-header d-flex align-items-center justify-content-between py-1-5">
        <div>
          <h4 class="mb-0 font-weight-bolder">Lead Distribution Settings</h4>
          <small class="text-muted">Control how incoming leads are assigned to your sales team</small>
        </div>
        <feather-icon icon="GitMergeIcon" size="22" class="text-primary" />
      </div>

      <b-overlay :show="loading" variant="light" :opacity="0.7" blur="2px" rounded="sm">
        <b-form @submit.prevent="save" class="px-2 pb-2">
          <b-row class="mt-2">

            <!-- LEFT COLUMN: Mode Selection -->
            <b-col cols="12" lg="5">
              <div class="section-label mb-1">
                <span class="section-title">Distribution Mode</span>
              </div>

              <div class="mode-options">
                <label
                  v-for="option in modeOptions"
                  :key="option.value"
                  class="mode-option"
                  :class="{ active: form.distribution_mode === option.value }"
                  @click="form.distribution_mode = option.value"
                >
                  <div class="mode-radio">
                    <input
                      type="radio"
                      :value="option.value"
                      v-model="form.distribution_mode"
                      class="d-none"
                    />
                    <div class="radio-indicator" :class="{ checked: form.distribution_mode === option.value }">
                      <div class="radio-dot" />
                    </div>
                  </div>
                  <div class="mode-content ml-1">
                    <div class="d-flex align-items-center">
                      <feather-icon :icon="option.icon" size="14" class="mr-50 text-primary" />
                      <span class="font-weight-bold">{{ option.label }}</span>
                    </div>
                    <small class="text-muted">{{ option.description }}</small>
                  </div>
                </label>
              </div>

              <!-- Specific salesperson picker -->
              <b-collapse :visible="form.distribution_mode === 'specific'">
                <div class="mt-1 p-1 bg-light rounded border-left border-primary" style="border-left-width:3px!important">
                  <b-form-group label="Assign all leads to" class="mb-0">
                    <v-select
                      v-model="form.specific_salesperson_id"
                      :options="salespersonOptions"
                      :reduce="o => o.value"
                      label="label"
                      placeholder="Select salesperson..."
                    />
                    <small v-if="salespersonOptions.length === 0" class="text-warning d-block mt-50">
                      <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
                      No active salespersons. Add them in the Salesperson tab first.
                    </small>
                  </b-form-group>
                </div>
              </b-collapse>

              <b-button
                type="submit"
                variant="primary"
                :disabled="saving"
                class="mt-2 px-2"
              >
                <b-spinner v-if="saving" small class="mr-50" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-50" />
                Save Settings
              </b-button>
            </b-col>

            <!-- DIVIDER -->
            <b-col cols="12" lg="1" class="d-none d-lg-flex justify-content-center">
              <div class="vertical-divider" />
            </b-col>
            <b-col cols="12" class="d-lg-none my-2">
              <hr class="m-0" />
            </b-col>

            <!-- RIGHT COLUMN: Per-salesperson settings -->
            <b-col cols="12" lg="6">
              <div class="section-label mb-1">
                <span class="section-title">Per-Salesperson Settings</span>
                <small class="text-muted d-block mt-25">
                  Weight applies in Weighted mode · Daily cap applies to all auto modes
                </small>
              </div>

              <div
                v-if="form.distribution_mode === 'manual'"
                class="empty-state text-center py-3"
              >
                <feather-icon icon="UserCheckIcon" size="32" class="text-muted mb-1" />
                <p class="text-muted mb-0">Manual mode — leads are unassigned on arrival.</p>
                <small class="text-muted">No per-salesperson settings apply.</small>
              </div>

              <div v-else-if="form.salesperson_settings.length === 0" class="empty-state text-center py-3">
                <feather-icon icon="UsersIcon" size="32" class="text-muted mb-1" />
                <p class="text-muted mb-0">No salespersons configured yet.</p>
                <small class="text-muted">Add them in the Salesperson tab to configure weights and caps.</small>
              </div>

              <div v-else>
                <b-table
                  class="position-relative"
                  :items="form.salesperson_settings"
                  :fields="spTableFields"
                  primary-key="salesperson_id"
                  responsive
                  show-empty
                  empty-text="No salespersons found."
                >
                  <!-- Column: Salesperson -->
                  <template #cell(name)="data">
                    <b-media vertical-align="center">
                      <template #aside>
                        <b-avatar
                          size="32"
                          :text="avatarText(data.item.name)"
                          variant="light-primary"
                        />
                      </template>
                      <span class="font-weight-bold d-block text-nowrap">{{ data.item.name }}</span>
                      <small class="text-muted">{{ data.item.email }}</small>
                    </b-media>
                  </template>

                  <!-- Column: Weight -->
                  <template #head(weight)>
                    Weight
                    <feather-icon
                      v-b-tooltip.hover
                      title="Higher weight = more leads. E.g. weight 3 gets 3× more leads than weight 1."
                      icon="InfoIcon"
                      size="12"
                      class="text-muted ml-25"
                    />
                  </template>
                  <template #cell(weight)="data">
                    <b-form-input
                      v-model.number="data.item.weight"
                      type="number"
                      min="1"
                      max="100"
                      size="sm"
                      style="width:100px"
                      :disabled="form.distribution_mode !== 'weighted'"
                    />
                  </template>

                  <!-- Column: Daily Cap -->
                  <template #head(daily_cap)>
                    Daily Cap
                    <feather-icon
                      v-b-tooltip.hover
                      title="Max leads this salesperson can receive per day. Leave blank for no limit."
                      icon="InfoIcon"
                      size="12"
                      class="text-muted ml-25"
                    />
                  </template>
                  <template #cell(daily_cap)="data">
                    <b-form-input
                      v-model.number="data.item.daily_cap"
                      type="number"
                      min="1"
                      placeholder="No limit"
                      size="sm"
                      style="width:110px"
                    />
                  </template>
                </b-table>
                <small class="text-muted">
                  <feather-icon icon="InfoIcon" size="11" class="mr-25" />
                  Weight field is only active in Weighted mode.
                </small>
              </div>
            </b-col>

          </b-row>
        </b-form>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BButton, BOverlay, BSpinner,
  BTable, BMedia, BAvatar, BRow, BCol, BCollapse,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import store from '@/store'

export default {
  components: {
    BCard, BForm, BFormGroup, BFormInput, BButton, BOverlay, BSpinner,
    BTable, BMedia, BAvatar, BRow, BCol, BCollapse,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    landingPageUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      form: {
        distribution_mode: 'auto',
        specific_salesperson_id: null,
        salesperson_settings: [],
      },
      salespersonOptions: [],
      spTableFields: [
        { key: 'name', label: 'Salesperson' },
        { key: 'weight', label: 'Weight', thStyle: { width: '130px' } },
        { key: 'daily_cap', label: 'Daily Cap', thStyle: { width: '150px' } },
      ],
      modeOptions: [
        {
          value: 'auto',
          label: 'Auto — Least Leads',
          icon: 'ZapIcon',
          description: 'Incoming leads go to the salesperson with the fewest leads.',
        },
        {
          value: 'weighted',
          label: 'Weighted',
          icon: 'BarChart2Icon',
          description: 'Each salesperson gets a share proportional to their weight.',
        },
        {
          value: 'manual',
          label: 'Manual',
          icon: 'EditIcon',
          description: 'Leads arrive unassigned — your team assigns them manually.',
        },
        {
          value: 'specific',
          label: 'Specific Salesperson',
          icon: 'UserIcon',
          description: 'All new leads always go to one fixed salesperson.',
        },
      ],
    }
  },
  created() {
    this.fetchSettings()
  },
  methods: {
    avatarText,
    async fetchSettings() {
      this.loading = true
      try {
        const response = await store.dispatch('menu-lead/getDistributionSettings', { uuid: this.landingPageUuid })
        const data = response.data
        this.form.distribution_mode = data.distribution_mode || 'auto'
        this.form.specific_salesperson_id = data.specific_salesperson_id || null
        this.form.salesperson_settings = (data.salesperson_settings || []).map(sp => ({
          salesperson_id: sp.salesperson_id,
          name: sp.name,
          email: sp.email || '',
          weight: sp.weight ?? 1,
          daily_cap: sp.daily_cap ?? null,
        }))
        this.salespersonOptions = this.form.salesperson_settings.map(sp => ({
          label: sp.name,
          value: sp.salesperson_id,
        }))
      } catch (e) {
        this.$bvToast.toast('Failed to load distribution settings.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (this.form.distribution_mode === 'specific' && !this.form.specific_salesperson_id) {
        this.$bvToast.toast('Please select a salesperson for Specific mode.', { title: 'Validation', variant: 'warning', solid: true })
        return
      }
      this.saving = true
      try {
        await store.dispatch('menu-lead/updateDistributionSettings', {
          uuid: this.landingPageUuid,
          data: {
            distribution_mode: this.form.distribution_mode,
            specific_salesperson_id: this.form.distribution_mode === 'specific' ? this.form.specific_salesperson_id : null,
            salesperson_settings: this.form.salesperson_settings.map(sp => ({
              salesperson_id: sp.salesperson_id,
              weight: sp.weight || 1,
              daily_cap: sp.daily_cap || null,
            })),
          },
        })
        this.$bvToast.toast('Distribution settings saved!', { title: 'Success', variant: 'success', solid: true })
      } catch (e) {
        const msg = e.response?.data?.message || 'Failed to save settings.'
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.py-1-5 {
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
}

.section-label {
  border-bottom: 2px solid #ebe9f1;
  padding-bottom: 0.5rem;
}

.section-title {
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #5e5873;
}

.mode-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mode-option {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  margin-bottom: 0;
  background: #fff;
}

.mode-option:hover {
  border-color: #7367f0;
  background-color: #f8f7ff;
}

.mode-option.active {
  border-color: #7367f0;
  background-color: #f3f2fe;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.15);
}

.radio-indicator {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #b9b3e5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  transition: border-color 0.15s ease;
}

.radio-indicator.checked {
  border-color: #7367f0;
}

.radio-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.15s ease;
}

.radio-indicator.checked .radio-dot {
  background: #7367f0;
}

.mode-content small {
  line-height: 1.4;
  display: block;
  margin-top: 2px;
}

.vertical-divider {
  width: 1px;
  background: #ebe9f1;
  height: 100%;
  min-height: 200px;
}

.empty-state {
  border: 1px dashed #ebe9f1;
  border-radius: 8px;
  background: #fafafa;
}
</style>
